/**
 * Reading and display preferences. Stored per device in localStorage (a per-viewer convenience)
 * and mirrored onto <html data-*> so CSS tokens react. Every read/write is guarded because storage
 * can be unavailable in private windows.
 */
import { storageKey } from "@shared/brand";
import { useEffect, useState } from "preact/hooks";

export type FontChoice = "atkinson" | "lexend" | "opendyslexic" | "system";
export type SizeChoice = "default" | "large" | "xl";
export type SpacingChoice = "default" | "comfortable" | "reading";
export type ThemeChoice = "auto" | "light" | "dark" | "cream";
export type MotionChoice = "auto" | "reduced" | "full";
export type ContrastChoice = "normal" | "high";

export interface Prefs {
  font: FontChoice;
  size: SizeChoice;
  spacing: SpacingChoice;
  theme: ThemeChoice;
  motion: MotionChoice;
  contrast: ContrastChoice;
  locale: string | null;
}

export const DEFAULT_PREFS: Prefs = {
  font: "atkinson",
  size: "default",
  spacing: "default",
  theme: "auto",
  motion: "auto",
  contrast: "normal",
  locale: null,
};

const KEY = storageKey("prefs");
const listeners = new Set<(p: Prefs) => void>();
let current: Prefs = load();

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function getPrefs(): Prefs {
  return current;
}

export function setPrefs(patch: Partial<Prefs>): void {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable */
  }
  applyPrefs(current);
  for (const l of listeners) l(current);
}

export function resetPrefs(): void {
  setPrefs({ ...DEFAULT_PREFS, locale: current.locale });
}

export function applyPrefs(p: Prefs = current): void {
  const h = document.documentElement;
  const set = (k: string, v: string | null) => {
    if (v === null) delete h.dataset[k];
    else h.dataset[k] = v;
  };
  set("font", p.font === "atkinson" ? null : p.font);
  set("size", p.size === "default" ? null : p.size);
  set("spacing", p.spacing === "default" ? null : p.spacing);
  set("theme", p.theme === "auto" ? null : p.theme);
  set("motion", p.motion === "auto" ? null : p.motion);
  set("contrast", p.contrast === "normal" ? null : p.contrast);
}

export function usePrefs(): [Prefs, (patch: Partial<Prefs>) => void] {
  const [p, setP] = useState(current);
  useEffect(() => {
    listeners.add(setP);
    return () => {
      listeners.delete(setP);
    };
  }, []);
  return [p, setPrefs];
}

/** True when motion should be minimised (user or OS). */
export function reducedMotion(): boolean {
  if (current.motion === "reduced") return true;
  if (current.motion === "full") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
